import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { AlertTriangle, Check, MapPin, Plus, RefreshCw, Search, UserPlus, X } from "react-feather";
import EmployeePicker from "../../users/components/EmployeePicker";
import EmployeeAvatar from "../../reports/oneStop/components/EmployeeAvatar";
import { useEmployeePhotoCache } from "../../masters/employees/useEmployeePhotoCache";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useHasPermission } from "../../auth/useClaims";
import {
  useAssignStandingPostMutation,
  useEndStandingPostMutation,
  useCreateStandingPostSlotMutation,
  useListStandingPostSlotsQuery,
  useListStandingPostsQuery,
  useListUnmappedStandingPostsQuery,
  useSeedStandingPostsMutation,
} from "../guardingApi";

// Office Attendance Posts (2026-09-15, Batch173).
//
// A "standing post" is the duty slot an office employee's kiosk fingerprint
// punch gets credited to. It is a CARRIER, not a record of where the person
// physically stood: payroll reads only presence per employee per day
// (dbo.fn_wrkingdays in the branch DBs emits emp_Code + date, with no
// Cust_Id/assignment), so which slot carries the day never reaches a payslip.
// The page therefore optimises for one question - "can the kiosk mark this
// person?" - rather than for posting accuracy.
//
// Two lists, because they need different actions: people who have a slot, and
// people who work here but do not (so the kiosk turns them away). New joiners
// with no duty history at all appear in neither and are added with the picker.

const fmtDate = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-";

const EnrolledPill = ({ isEnrolled }) =>
  isEnrolled ? (
    <span className="badge badge-linesuccess">
      <Check size={11} /> Enrolled
    </span>
  ) : (
    // Not a cosmetic gap: without a fingerprint on file the kiosk cannot
    // identify them at all, so the slot alone does nothing for them.
    <span className="badge badge-linewarning" title="No fingerprint enrolled - the kiosk cannot recognise this employee yet">
      <AlertTriangle size={11} /> Not enrolled
    </span>
  );

// Name + code with the enrolment photo, as everywhere else an employee is named.
// photoUrls comes from the page's single useEmployeePhotoCache, so someone listed
// in both tables and the seeding result still costs one photo request.
const EmployeeCell = ({ row, photoUrls }) => (
  <div className="d-flex align-items-center gap-2">
    <EmployeeAvatar name={row.employeeName} photoUrl={photoUrls[row.employeeRecordGuid]} />
    <div style={{ minWidth: 0 }}>
      <div className="fw-semibold">{row.employeeName}</div>
      <div className="small text-muted">{row.empCode}</div>
    </div>
  </div>
);

// Slot picker. Taken slots are listed, disabled, with their holder named,
// because the server refuses two people on one slot (the punch upsert MERGEs
// on the slot key, so sharing would overwrite one person's attendance). Showing
// them greyed explains the gap; hiding them would just look like missing data.
const SlotPickerModal = ({ show, onHide, employee, photoUrl, branchId, onSave, isSaving }) => {
  const { data: slots = [], isFetching } = useListStandingPostSlotsQuery(
    { branchId },
    { skip: !show || !branchId }
  );
  const [custId, setCustId] = useState("");
  const [search, setSearch] = useState("");
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPost, setNewPost] = useState({ name: "", area: "", dayNight: "Day" });
  const [createSlot, { isLoading: isCreatingSlot }] = useCreateStandingPostSlotMutation();

  // Areas offered are the ones already in use at this customer's own slots: they
  // come from Area Master (the server rejects anything else), and a free-text box
  // would just invite a typo that makes the new post invisible to every
  // attendance read, which matches area as exact text.
  const areaOptions = useMemo(() => {
    const seen = new Set();
    slots.forEach((s) => {
      if (custId && String(s.custId) !== custId) return;
      if (s.assignmentArea) seen.add(s.assignmentArea.trim());
    });
    return [...seen].sort();
  }, [slots, custId]);

  const handleCreateSlot = async () => {
    try {
      const created = await createSlot({
        branchId,
        custId: Number(custId),
        assignmentName: newPost.name,
        assignmentArea: newPost.area || null,
        dayNight: newPost.dayNight,
      }).unwrap();
      toast.success(`Post "${created.assignmentName}" created`);
      setNewPost({ name: "", area: "", dayNight: "Day" });
      setShowNewPost(false);
    } catch (error) {
      toast.error(error?.message || "Could not create the post");
    }
  };

  const customers = useMemo(() => {
    const seen = new Map();
    slots.forEach((s) => {
      if (!seen.has(String(s.custId))) seen.set(String(s.custId), s.custLabel || `Customer ${s.custId}`);
    });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [slots]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return slots.filter((s) => {
      if (custId && String(s.custId) !== custId) return false;
      if (!term) return true;
      return (
        (s.assignmentName || "").toLowerCase().includes(term) ||
        (s.assignmentArea || "").toLowerCase().includes(term)
      );
    });
  }, [slots, custId, search]);

  const freeCount = visible.filter((s) => s.isFree).length;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-5 d-flex align-items-center gap-2">
          {employee && <EmployeeAvatar name={employee.employeeName} photoUrl={photoUrl} size="lg" />}
          <span>
            Duty slot for {employee?.employeeName || "employee"}
            {employee?.empCode ? <span className="text-muted fw-normal"> · {employee.empCode}</span> : null}
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          Any free slot works - payroll counts the day, not the post. Pick the one that matches their real job so the
          duty list still reads sensibly.
        </p>
        <div className="row g-2 mb-3">
          <div className="col-md-6">
            <select className="form-select form-select-sm" value={custId} onChange={(e) => setCustId(e.target.value)}>
              <option value="">All our premises</option>
              {customers.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search slot or area..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {isFetching ? (
          <p className="text-muted mb-0">Loading slots...</p>
        ) : visible.length === 0 ? (
          <p className="text-muted mb-0">No slots match these filters.</p>
        ) : (
          <>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="small text-muted">
                {freeCount} free of {visible.length} shown
              </div>
              {/* A post that does not exist yet (a brand-new role) is created right
                  here, under this page's own permission - sending the user to
                  Masters > Customers > Guarding would demand Deployment.Edit as
                  well, and holding the action permission must not require a second
                  one on a master. The endpoint wraps the canonical single-slot
                  upsert, so it cannot disturb the customer's other slots. */}
              {custId && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowNewPost((v) => !v)}
                >
                  <Plus size={13} className="me-1" /> New post
                </button>
              )}
            </div>
            {showNewPost && (
              <div className="border rounded p-2 mb-2 bg-light">
                <div className="row g-2 align-items-end">
                  <div className="col-md-5">
                    <label className="form-label small mb-1">Post name</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="e.g. CREDIT CONTROLLER 9"
                      value={newPost.name}
                      onChange={(e) => setNewPost((p) => ({ ...p, name: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small mb-1">Area</label>
                    <select
                      className="form-select form-select-sm"
                      value={newPost.area}
                      onChange={(e) => setNewPost((p) => ({ ...p, area: e.target.value }))}
                    >
                      <option value="">No area</option>
                      {areaOptions.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small mb-1">Shift</label>
                    <select
                      className="form-select form-select-sm"
                      value={newPost.dayNight}
                      onChange={(e) => setNewPost((p) => ({ ...p, dayNight: e.target.value }))}
                    >
                      <option value="Day">Day</option>
                      <option value="Night">Night</option>
                      <option value="Both">Both</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary w-100"
                      disabled={!newPost.name.trim() || isCreatingSlot}
                      onClick={handleCreateSlot}
                    >
                      {isCreatingSlot ? "Creating..." : "Create"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="table-responsive" style={{ maxHeight: 360, overflowY: "auto" }}>
              <table className="table table-sm align-middle mb-0">
                <tbody>
                  {visible.map((s) => (
                    <tr key={`${s.custId}-${s.assignmentId}`} className={s.isFree ? "" : "text-muted"}>
                      <td>
                        <div className="fw-semibold">{s.assignmentName || `Slot ${s.assignmentId}`}</div>
                        <div className="small text-muted">
                          {s.custLabel} &middot; {s.assignmentArea || "no area"}
                          {s.slotDayNight ? ` · ${s.slotDayNight}` : ""}
                        </div>
                      </td>
                      <td className="text-end" style={{ width: 200 }}>
                        {s.isFree ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            disabled={isSaving}
                            onClick={() => onSave(s)}
                          >
                            Use this slot
                          </button>
                        ) : (
                          <span className="small">held by {s.heldByName || "someone else"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

const OfficeAttendancePostsPage = () => {
  const { selectedBranchId } = useBranchFilter();
  // Posts are one branch's door device. It used to fall back to branch 1 under "All
  // branches", silently showing Mombasa's posts (2026-10-02) - now it asks for a branch.
  const branchId = selectedBranchId;
  // Matches the backend's own "Attendance.ManagePosts|Attendance.View" policy on
  // every action. Gating on ManagePosts alone made the page read-only for
  // everybody, including System Administrator: that permission is seeded by
  // Batch173b, so until that batch runs it exists nowhere at all.
  //
  // Both hooks are called unconditionally and combined afterwards - written as
  // `a() || b()` the second call short-circuits whenever the first is true, so
  // the hook count changes between renders (a real rules-of-hooks violation, the
  // same one already documented in useGuardingPermissions.js).
  const canManagePosts = useHasPermission("Attendance.ManagePosts");
  const canViewAttendance = useHasPermission("Attendance.View");
  const canManage = canManagePosts || canViewAttendance;

  const [search, setSearch] = useState("");
  const [picker, setPicker] = useState(null); // { employeeRecordGuid, employeeName, empCode }
  const [newEmployee, setNewEmployee] = useState(null);
  const [seedResult, setSeedResult] = useState(null);

  const { data: posts = [], isLoading } = useListStandingPostsQuery(
    { branchId, search: search || undefined },
    { skip: branchId == null }
  );
  const { data: unmapped = [], isLoading: isLoadingUnmapped } = useListUnmappedStandingPostsQuery(
    { branchId },
    { skip: branchId == null }
  );

  // Everyone this page names, in one list, so each photo is fetched once. The
  // picker is included because a new joiner from the search box is on no list.
  const photoRows = useMemo(
    () => [...unmapped, ...posts, ...(seedResult?.needsDecision || []), ...(picker ? [picker] : [])],
    [unmapped, posts, seedResult, picker]
  );
  const photoUrls = useEmployeePhotoCache(photoRows);

  const [assignPost, { isLoading: isAssigning }] = useAssignStandingPostMutation();
  const [endPost] = useEndStandingPostMutation();
  const [seedPosts, { isLoading: isSeeding }] = useSeedStandingPostsMutation();

  const notEnrolledCount = posts.filter((p) => !p.isEnrolled).length;

  const handleSaveSlot = async (slot) => {
    try {
      await assignPost({
        employeeRecordGuid: picker.employeeRecordGuid,
        branchId,
        custId: slot.custId,
        assignmentId: slot.assignmentId,
      }).unwrap();
      toast.success(`${picker.employeeName || "Employee"} → ${slot.assignmentName || "slot"}`);
      setPicker(null);
      setNewEmployee(null);
    } catch (error) {
      toast.error(error?.message || "Could not save the standing post");
    }
  };

  const handleEnd = async (post) => {
    try {
      await endPost(post.standingPostId).unwrap();
      toast.success(`${post.employeeName || "Employee"} removed from ${post.assignmentName || "their slot"}`);
    } catch (error) {
      toast.error(error?.message || "Could not end the standing post");
    }
  };

  const handleSeed = async () => {
    try {
      const result = await seedPosts({ branchId }).unwrap();
      setSeedResult(result);
    } catch (error) {
      toast.error(error?.message || "Could not fill from history");
    }
  };

  if (branchId == null) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <h4 className="mb-3">Office Attendance Posts</h4>
          <div className="alert alert-info mb-0">
            Office posts belong to one branch&apos;s door device - pick a branch in the header to see and manage them.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h4 className="mb-1">Office Attendance Posts</h4>
            <p className="text-muted mb-0" style={{ maxWidth: 720 }}>
              The duty slot each office employee's fingerprint punch is credited to. Payroll counts the day, not the
              slot - so what matters here is that everyone who uses the door device has one, and that no two people
              share one.
            </p>
          </div>
          {canManage && (
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleSeed} disabled={isSeeding}>
              <RefreshCw size={14} className="me-1" />
              {isSeeding ? "Filling..." : "Fill from history"}
            </button>
          )}
        </div>

        {notEnrolledCount > 0 && (
          <div className="alert alert-warning d-flex align-items-center gap-2 py-2">
            <AlertTriangle size={16} />
            <span>
              {notEnrolledCount} of {posts.length} have a slot but <strong>no fingerprint enrolled</strong> - the kiosk
              cannot recognise them yet.
            </span>
          </div>
        )}

        {/* The backlog first: these are the people the kiosk turns away today,
            which is the only actionable problem on this page. */}
        <div className="card mb-3">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h5 className="mb-0">
              Not set up yet
              {unmapped.length > 0 && <span className="badge badge-linewarning ms-2">{unmapped.length}</span>}
            </h5>
            <span className="text-muted small">Working at our premises with no slot</span>
          </div>
          <div className="card-body">
            {isLoadingUnmapped ? (
              <p className="text-muted mb-0">Loading...</p>
            ) : unmapped.length === 0 ? (
              <p className="text-muted mb-0">Everyone with recent duty here has a slot.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th className="text-center">Days worked</th>
                      <th className="text-center">Slots used</th>
                      <th>Last seen</th>
                      <th>Fingerprint</th>
                      {canManage && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {unmapped.map((e) => (
                      <tr key={e.employeeRecordGuid}>
                        <td>
                          <EmployeeCell row={e} photoUrls={photoUrls} />
                        </td>
                        <td className="text-center">{e.daysWorked}</td>
                        <td className="text-center">
                          {/* Several slots is why they were left for a human: the
                              seeding run only auto-assigns an unambiguous one. */}
                          {e.slotsTouched > 1 ? (
                            <span className="badge badge-lineinfo" title="Rotated between slots - needs a decision">
                              {e.slotsTouched}
                            </span>
                          ) : (
                            e.slotsTouched
                          )}
                        </td>
                        <td>{fmtDate(e.lastSeen)}</td>
                        <td>
                          <EnrolledPill isEnrolled={e.isEnrolled} />
                        </td>
                        {canManage && (
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={() =>
                                setPicker({
                                  employeeRecordGuid: e.employeeRecordGuid,
                                  employeeName: e.employeeName,
                                  empCode: e.empCode,
                                })
                              }
                            >
                              <MapPin size={13} className="me-1" /> Assign slot
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {canManage && (
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="mb-2">
                <UserPlus size={15} className="me-1" /> New joiner
              </h6>
              <p className="text-muted small mb-2">
                Somebody with no duty history here yet - search them by name or code, then pick their slot.
              </p>
              <div className="row g-2 align-items-end">
                <div className="col-md-8">
                  <EmployeePicker value={newEmployee} onChange={setNewEmployee} />
                </div>
                <div className="col-md-4">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={!newEmployee}
                    onClick={() =>
                      setPicker({
                        employeeRecordGuid: newEmployee.value,
                        employeeName: newEmployee.employee
                          ? `${newEmployee.employee.empName || ""} ${newEmployee.employee.empLastName || ""}`.trim()
                          : newEmployee.label,
                        empCode: newEmployee.employee?.empCode,
                      })
                    }
                  >
                    <MapPin size={13} className="me-1" /> Pick slot
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h5 className="mb-0">
              Assigned
              {posts.length > 0 && <span className="badge badge-linesuccess ms-2">{posts.length}</span>}
            </h5>
            <div className="input-icon-start position-relative" style={{ maxWidth: 260 }}>
              <Search size={14} className="position-absolute" style={{ left: 8, top: 9, opacity: 0.5 }} />
              <input
                type="text"
                className="form-control form-control-sm ps-4"
                placeholder="Search name, code or slot..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="card-body">
            {isLoading ? (
              <p className="text-muted mb-0">Loading...</p>
            ) : posts.length === 0 ? (
              <p className="text-muted mb-0">
                No standing posts yet. "Fill from history" sets up everyone whose slot is unambiguous.
              </p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Slot</th>
                      <th>Fingerprint</th>
                      <th>Last punch</th>
                      <th>Since</th>
                      {canManage && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((p) => (
                      <tr key={p.standingPostId}>
                        <td>
                          <EmployeeCell row={p} photoUrls={photoUrls} />
                        </td>
                        <td>
                          <div>{p.assignmentName || `Slot ${p.assignmentId}`}</div>
                          <div className="small text-muted">
                            {p.custLabel} &middot; {p.assignmentArea || "no area"}
                            {p.source === "Seeded" && (
                              <span className="badge badge-linedark ms-2" title="Set automatically from duty history">
                                from history
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <EnrolledPill isEnrolled={p.isEnrolled} />
                        </td>
                        <td>{fmtDate(p.lastPunchDate)}</td>
                        <td>{fmtDate(p.fromDate)}</td>
                        {canManage && (
                          <td className="text-end text-nowrap">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() =>
                                setPicker({
                                  employeeRecordGuid: p.employeeRecordGuid,
                                  employeeName: p.employeeName,
                                  empCode: p.empCode,
                                })
                              }
                            >
                              Change
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              title="End this standing post"
                              onClick={() => handleEnd(p)}
                            >
                              <X size={13} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <SlotPickerModal
        show={!!picker}
        onHide={() => setPicker(null)}
        employee={picker}
        photoUrl={picker ? photoUrls[picker.employeeRecordGuid] : undefined}
        branchId={branchId}
        onSave={handleSaveSlot}
        isSaving={isAssigning}
      />

      {/* Seeding result: the counts matter less than the list of people it
          deliberately did NOT touch, which is the work left for a human. */}
      <Modal show={!!seedResult} onHide={() => setSeedResult(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-5">Filled from history</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {seedResult?.applied?.length > 0 && (
            <table className="table table-sm mb-3">
              <thead>
                <tr>
                  <th>Verdict</th>
                  <th className="text-center">Employees</th>
                  <th className="text-center">Now set</th>
                </tr>
              </thead>
              <tbody>
                {seedResult.applied.map((a) => (
                  <tr key={a.verdict}>
                    <td>{a.verdict}</td>
                    <td className="text-center">{a.employees}</td>
                    <td className="text-center">{a.nowMapped}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {seedResult?.needsDecision?.length > 0 ? (
            <>
              <h6 className="mb-1">Still needs a decision ({seedResult.needsDecision.length})</h6>
              <p className="text-muted small">
                These rotated between slots, or share a slot with someone still using it - so no single slot is
                obviously theirs. Assign them from the list above.
              </p>
              <div className="table-responsive" style={{ maxHeight: 300, overflowY: "auto" }}>
                <table className="table table-sm mb-0">
                  <tbody>
                    {seedResult.needsDecision.map((d) => (
                      <tr key={d.employeeRecordGuid}>
                        <td>
                          <EmployeeCell row={d} photoUrls={photoUrls} />
                        </td>
                        <td>
                          <div className="small">{d.assignmentName || `Slot ${d.assignmentId}`}</div>
                          <div className="small text-muted">
                            {d.daysOnSlot} of {d.totalDays} days
                            {d.sharePct != null ? ` · ${d.sharePct}%` : ""}
                          </div>
                        </td>
                        <td className="text-end">
                          <span className="badge badge-lineinfo">{d.verdict}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="text-muted mb-0">Nothing left needing a decision.</p>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default OfficeAttendancePostsPage;
